/**
 * Admin table view for listing and filtering spaces (question instances):
 * `<SpacesTable>`, `useSpaceList`, and the `useSpaceResultsToSpaces`
 * translation hook.
 *
 * `<SpacesTable>` is config-driven via `SpacesTableConfig`: features push
 * `columns`, `filters`, and an optional `searchFilter` into the config. The
 * component handles client-side pagination (page size 10) and resets to page 0
 * whenever the search or filter state changes. `useSpaceList` returns a
 * sorted `Space[]` (the legacy client-side row shape) translated from the
 * wire-level `SpaceResult` via `useSpaceResultsToSpaces`. These types exist
 * separately for historical reasons and may be unified in the future.
 */
import { displayTextOf, originalTextOf } from '@repo/shared/orpc/multi-language';
import type { SpaceResult, SpaceStatus } from '@repo/shared/orpc/procedures/spaces';
import type { UserProfile } from '@repo/shared/orpc/types';
import { createThemedStyles } from '@repo/theme/styles';
import { PaginationRow } from 'component/dashboard/pagination-row';
import type { TableColumn } from 'component/dashboard/table-grid';
import { TableGrid } from 'component/dashboard/table-grid';
import { useLanguage } from 'component/translation';
import { useAllSpaces } from 'data/spaces';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useSessionDataValues } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { type TLabel, t } from 'util/stdtypes';

import { HorizBox } from './basics';
import { SearchField } from './form';
import { Heading } from './text';

/**
 * Decides whether a row survives the filter, given the filter's own current
 * session-data value. `sessionKey` names the key that value is read from, so
 * the table can subscribe on the filter's behalf; a filter that depends on no
 * session state omits it and is passed `undefined`.
 */
export type SpaceFilterFunc = (selected: unknown, item: Space) => boolean;
export type SpaceFilter = {
    key: string;
    widget: React.ComponentType;
    sessionKey?: string;
    filter: SpaceFilterFunc;
};

export type SpaceSearchFunc = (item: Space, searchTerm: string) => boolean;

const SPACE_TABLE_PAGE_SIZE = 10;

export function SpacesTable({ itemList }: { itemList: Space[] | undefined }) {
    const s = useSpacesTableStyles();
    const {
        filters,
        spaceTableRightWidgets,
        emptyTableWidget: EmptyTableWidget,
        searchFilter,
        tableTitleLabel,
        columns,
    } = useConfig<SpacesTableConfig>();
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(0);

    const activeFilters = filters ?? [];
    const selectedByKey = useSessionDataValues(activeFilters.flatMap((filter) => filter.sessionKey ?? []));

    const loading = itemList === undefined;
    const items = itemList ?? [];

    const filteredItemList = items.filter((item) =>
        activeFilters.every((filter) => filter.filter(filter.sessionKey ? selectedByKey[filter.sessionKey] : undefined, item)),
    );
    const searchedItemList = searchFilter ? filteredItemList.filter((item) => searchFilter(item, search)) : filteredItemList;

    // Reset to first page when filters or search change
    useEffect(() => {
        setCurrentPage(0);
    }, [search, searchedItemList.length]);

    const paginatedItemList = searchedItemList.slice(currentPage * SPACE_TABLE_PAGE_SIZE, (currentPage + 1) * SPACE_TABLE_PAGE_SIZE);

    return (
        <View style={s.table}>
            <HorizBox center spread>
                <Heading label={tableTitleLabel} type='large' weight='medium' />
                {searchFilter && (
                    <SearchField onChange={setSearch} placeholderLabel={t('space.table.search', 'Search...')} size='small' value={search} />
                )}
            </HorizBox>
            <HorizBox center spread>
                <HorizBox gap={16}>
                    {filters?.map((filter) => (
                        <filter.widget key={filter.key} />
                    ))}
                </HorizBox>
                <HorizBox>
                    {spaceTableRightWidgets?.map((widget, i) =>
                        // eslint-disable-next-line react/no-array-index-key
                        React.createElement(widget, { key: i }),
                    )}
                </HorizBox>
            </HorizBox>
            <TableGrid
                columns={columns}
                emptyWidget={
                    EmptyTableWidget && (
                        <EmptyTableWidget filteredItemList={filteredItemList} itemList={items} searchedItemList={searchedItemList} />
                    )
                }
                itemList={paginatedItemList}
                loading={loading}
            />
            {searchedItemList.length > SPACE_TABLE_PAGE_SIZE && (
                <PaginationRow
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    pageSize={SPACE_TABLE_PAGE_SIZE}
                    totalItems={searchedItemList.length}
                />
            )}
        </View>
    );
}

const useSpacesTableStyles = createThemedStyles((tokens) => ({
    table: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        borderColor: tokens.colors.colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
    },
}));

export type SpacesTableListWidgetProps = {
    itemList: Space[];
    filteredItemList?: Space[];
    searchedItemList?: Space[];
};

export type SpacesTableConfig = {
    spaceTableRightWidgets: React.FunctionComponent[];
    filters: SpaceFilter[];
    searchFilter?: SpaceSearchFunc;
    columns: TableColumn<Space>[];
    emptyTableWidget?: React.ComponentType<SpacesTableListWidgetProps>;
    tableTitleLabel: TLabel;
};

export type Space = {
    key: string;
    name: string;
    // Set by `useSpaceResultsToSpaces`; absent from callers that build a
    // `Space` directly (ranking-eval, tests, stories), which have no source
    // language to distinguish from `name` in the first place.
    sourceName?: string;
    structureKey: string;
    id?: string;
    from?: string;
    fromPreview?: UserProfile;
    commentCount?: number;
    format?: string | null;
    status?: SpaceStatus;
    time?: number;
    isHeated?: boolean;
    lastActivity?: number;
};

/**
 * Translate a list of wire-level `SpaceResult`s into the legacy client-side
 * `Space` row shape used by the dashboards. Stable across renders when the
 * input is structurally unchanged so consumers can pass the result through
 * `useMemo`-sensitive hooks without re-running. The wire `SpaceResult`
 * already carries the resolved creator profile, so no follow-up profile lookup
 * is needed.
 *
 * `name` resolves to the viewer's own locale, matching what the space's public
 * page shows. A multi-language silo serves several business units from one
 * space list (SRG: RTS, SRF, RSI, RTR, SWI), so the untranslated name would
 * otherwise leave a dashboard listing rows in whichever language each space
 * happened to be authored in. `sourceName` keeps the untranslated value
 * alongside it — not a stable "original" (an editor can rename a space after
 * creation, same as `name`), but the language `space.translations` was
 * generated from, and worth searching independently of the viewer's own
 * locale. See `searchSpace` in `feature/editorial-dash/spaces.tsx`.
 */
export function useSpaceResultsToSpaces(results: SpaceResult[]): Space[] {
    const isoLocale = useLanguage();
    return useMemo(
        () =>
            results.map((space) => ({
                key: space.spaceKey,
                name: displayTextOf(space.name, isoLocale),
                sourceName: originalTextOf(space.name),
                structureKey: 'question',
                id: space.id,
                status: space.status,
                time: new Date(space.createdAt).getTime(),
                from: space.createdBy.profileKey,
                fromPreview: space.createdBy,
                commentCount: space.commentCount,
                format: space.format ?? undefined,
                isHeated: space.isHeated,
                lastActivity: space.lastActivity !== undefined ? new Date(space.lastActivity).getTime() : undefined,
            })),
        [results, isoLocale],
    );
}

export function useSpaceList(): Space[] | undefined {
    const { spaces, isLoading } = useAllSpaces();
    const translated = useSpaceResultsToSpaces(spaces);
    if (isLoading) return undefined;
    return translated;
}
